import {
  AppConfigProvider,
  AppMachineProvider,
  CacheProvider,
  FullscreenDismissBlocker,
  ServiceWorkerHandler,
  useScrollRestoration,
  type AppConfig,
} from "@indietabletop/appkit";
import "@indietabletop/appkit/globals.css.ts";
import type { ReactNode } from "react";
import { flushSync } from "react-dom";
import { ErrorBoundary } from "react-error-boundary";
import { Toaster } from "react-hot-toast";
import { Router, useLocation, type AroundNavHandler } from "wouter";
import { machine } from "./AppMachine";
// import { CacheProvider } from "./CacheProvider";
// import { AppMachineProvider } from "./AppMachine";

export function ScrollRestoration(props: { neverRestore: string[] }) {
  const { neverRestore } = props;
  const [pathname] = useLocation();
  useScrollRestoration(pathname, { neverRestore });

  return null;
}

function getNavDirection(from: string, to: string) {
  const normalize = (p: string) =>
    p
      .replace(/^\/|\/$/g, "") // strip leading/trailing slashes
      .split("/")
      .filter(Boolean);

  const fromParts = normalize(from);
  const toParts = normalize(to);

  const isPrefix = (shorter: string[], longer: string[]) =>
    shorter.length < longer.length &&
    shorter.every((segment, i) => segment === longer[i]);

  if (isPrefix(fromParts, toParts)) {
    return "forward";
  }

  if (isPrefix(toParts, fromParts)) {
    return "backward";
  }

  return "forward";
}

const aroundNav: AroundNavHandler = (navigate, nextPathname, options) => {
  // Transitions are enabled by default, but can be switched off by specifying
  // <Link transition={false} /> where necessary.
  const transitionDisabled = options?.transition === false;

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!!document.startViewTransition && !transitionDisabled) {
    const currentPathname = window.location.pathname;

    const direction = getNavDirection(currentPathname, nextPathname);

    document.documentElement.dataset.navDirection = direction;

    const transition = document.startViewTransition(() => {
      flushSync(() => {
        navigate(nextPathname, options);
      });
    });

    transition.finished.then(
      () => {
        delete document.documentElement.dataset.navDirection;
      },
      (error: unknown) => void console.error(error),
    );
  } else {
    navigate(nextPathname, options);
  }
};

/**
 * Encapsulates common configuration of Indie Tabletop Club apps:
 *
 * - Root error boundary
 * - Custom scroll restoration
 * - App config provider
 * - Fullscreen dismiss blocker
 * - Service worker handler
 */
export function AppkitApp(props: {
  config: AppConfig;
  neverRestoreScrollFor: string[];
  children: ReactNode;
  exceptionFallback: ReactNode;
  swPath?: string;
}) {
  const {
    config,
    // neverRestoreScrollFor,
    children,
    exceptionFallback,
    swPath = "/sw.js",
  } = props;

  return (
    <ErrorBoundary fallback={exceptionFallback}>
      {/* <ScrollRestoration neverRestore={neverRestoreScrollFor} /> */}
      <Router aroundNav={aroundNav}>
        <AppConfigProvider config={config}>
          <FullscreenDismissBlocker />
          <ServiceWorkerHandler path={swPath}>
            <AppMachineProvider value={machine}>
              <CacheProvider>{children}</CacheProvider>
            </AppMachineProvider>
          </ServiceWorkerHandler>
          <Toaster />
        </AppConfigProvider>
      </Router>
    </ErrorBoundary>
  );
}
